import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// Image URLs for the page
const IMAGES = {
  hero: '/hero-activities.jpg',
  investing: 'https://customer-assets.emergentagent.com/job_4992c407-209d-4ffe-8201-36194c9609e6/artifacts/j1c77g4l_VetterTec%20Workshop%20with%20Tube%20bundle%20dryers%20cmyk300.jpg',
  valueCreation: 'https://customer-assets.emergentagent.com/job_69649056-df98-4ca6-bbd3-c5d20803d8d9/artifacts/4hrln9d3_image00010.jpeg',
  facility: 'https://customer-assets.emergentagent.com/job_69649056-df98-4ca6-bbd3-c5d20803d8d9/artifacts/qygpnduq_image00002.png',
  tubeBundles: 'https://customer-assets.emergentagent.com/job_4992c407-209d-4ffe-8201-36194c9609e6/artifacts/awdqd40l_VetterTec%20Workshop_Push-in%20of%20tubes%20into%20the%20tube%20bundle.jpg',
  mechanic: 'https://customer-assets.emergentagent.com/job_4992c407-209d-4ffe-8201-36194c9609e6/artifacts/l60db05f_VetterTec%20Workshop_Mechanic%20at%20Screw%20Press%20basket.JPG',
  craftsmanCompass: 'https://customer-assets.emergentagent.com/job_69649056-df98-4ca6-bbd3-c5d20803d8d9/artifacts/ehkuvbsr_image00007.jpeg',
  mechanicOverview: 'https://customer-assets.emergentagent.com/job_4992c407-209d-4ffe-8201-36194c9609e6/artifacts/pfowt3uq_VetterTec%20Workshop_Mechanic%20at%20Screw%20Press.jpg',
};

export const Activities = () => {
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState('investment');
  const bannerRef = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    if (imageRef.current) {
      gsap.to(imageRef.current, {
        yPercent: 20,
        ease: 'none',
        scrollTrigger: {
          trigger: bannerRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: 1
        }
      });
    }

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  // Floating CTA buttons
  const ctaButtons = [
    { label: language === 'fr' ? 'INVESTIR AVEC NOUS' : 'INVEST WITH US', link: '/contact?type=invest' },
    { label: language === 'fr' ? 'TRAVAILLER AVEC NOUS' : 'WORK WITH US', link: '/contact?type=work' },
    { label: language === 'fr' ? 'DEVENIR PARTENAIRE' : 'PARTNER WITH US', link: '/contact?type=partner' }
  ];

  // Investment section content blocks
  const investmentBlocks = [
    {
      id: 'capital-partners',
      title: 'Ocean Peak Capital Partners',
      content: language === 'fr'
        ? "Ocean Peak Capital Partners est un fonds à capital permanent géré par Tygrow SAS et conseillé par Ocean Peak Capital. Le fonds investit dans tous les secteurs, stratégies et géographies où Ocean Peak opère, offrant une exposition directe à tous les investissements réalisés par la plateforme. Les associés du Groupe ont investi de manière significative dans Ocean Peak Capital Partners, et le fonds sert de véhicule pour réinvestir les profits d'Ocean Peak Group."
        : "Ocean Peak Capital Partners is a permanent capital fund managed by Tygrow SAS and advised by Ocean Peak Capital. The fund invests across all sectors, strategies, and geographies where Ocean Peak operates, providing direct exposure to every investment made by the platform. All Group partners are personally invested in Ocean Peak Capital Partners, and the fund serves as the reinvestment vehicle for Ocean Peak Group's profits into current and future opportunities.",
      image: IMAGES.investing
    },
    {
      id: 'moret',
      title: 'Moret Industries Group',
      content: language === 'fr'
        ? "Moret Industries est un groupe industriel diversifié visant à acquérir et développer sur le long terme des \u00ab Micro-OEMs \u00bb actifs dans des marchés de niche au sein de l'écosystème industriel. Moret possède actuellement un portefeuille de 5 entreprises et est dirigé par les associés d'Ocean Peak (qui occupent des postes exécutifs)."
        : "Moret Industries is a diversified platform focused on acquiring and developing niche OEMs within the industrial ecosystem over the long term. Moret owns a portfolio of small and midsized companies, and is led by an executive team composed of Ocean Peak partners and officers.",
      image: IMAGES.tubeBundles
    },
    {
      id: 'team',
      title: language === 'fr' ? "Notre équipe d'investissement" : 'Our Investment Team',
      content: language === 'fr'
        ? "Notre équipe combine expertise financière, stratégique et industrielle. Nous avons investi et sommes intervenus dans des situations de croissance, de rachat et de restructuration, où nous avons développé au fil du temps une approche singulière de la transformation des entreprises industrielles."
        : "Our team combines financial, strategic, and industrial expertise. We have invested in and operated across growth, buyout, and restructuring situations, developing over time a proprietary approach to transforming industrial businesses.",
      image: IMAGES.mechanic
    },
    {
      id: 'track-record',
      title: language === 'fr' ? 'Historique à long terme' : 'Long-Term Track Record',
      content: language === 'fr'
        ? "Notre équipe a investi plus d'un milliard d'euros et apporte plus d'un siècle d'expérience cumulée dans l'analyse et la transformation d'entreprises industrielles dans les domaines de la stratégie, des opérations, de la finance et des ressources humaines dans les industries stratégiques : Aérospatial et Défense, Énergie, Santé, et Agro-alimentaire."
        : "Our team has invested over €1Bn and brings a combined experience of more than 100 years in analysing and transforming industrial businesses across strategy, operations, finance, and human resources.",
      content2: language === 'fr'
        ? "Nous avons également une connaissance approfondie des secteurs Aérospatial et Défense, Énergie et services publics, Santé et Alimentation et Agriculture."
        : "We also have deep sector knowledge in Aerospace & Defence, Energy & Utilities, Healthcare, and Food & Agriculture.",
      image: IMAGES.craftsmanCompass
    }
  ];

  return (
    <div className="min-h-screen bg-technical-white">
      {/* Floating CTA Buttons */}
      <div className="fixed right-8 top-1/3 z-40 flex flex-col gap-3 hidden lg:flex">
        {ctaButtons.map((btn, idx) => (
          <Link
            key={idx}
            to={btn.link}
            className="text-white text-xs font-bold tracking-wider px-5 py-3 bg-light-blue hover:bg-abyssal-blue transition-all duration-300 shadow-lg whitespace-nowrap"
            style={{ fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", letterSpacing: '1px', fontSize: '10px' }}
          >
            {btn.label}
          </Link>
        ))}
      </div>

      {/* Hero Banner */}
      <section
        id="activities-hero"
        ref={bannerRef}
        className="relative h-[500px] flex flex-col overflow-hidden"
        style={{ backgroundColor: '#0a1f35' }}
      >
        <div
          ref={imageRef}
          className="absolute inset-0 bg-cover bg-center scale-110"
          style={{
            backgroundImage: `url(${IMAGES.hero})`
          }}
        />
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(10, 31, 53, 0.4)' }} />

        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-4">
          <h1
            className="text-5xl lg:text-6xl text-white mb-8 text-center"
            style={{
              fontFamily: "'Bank Gothic', 'Impact', sans-serif",
              fontWeight: 900,
              letterSpacing: '0.02em',
              textTransform: 'uppercase'
            }}
          >
            {language === 'fr' ? 'ACTIVITÉS' : 'ACTIVITIES'}
          </h1>
          <p
            className="text-xl lg:text-2xl text-white text-center max-w-4xl mx-auto font-bold"
            style={{
              fontFamily: "'Eurostile', 'Arial', sans-serif",
              lineHeight: 1.6
            }}
          >
            {language === 'fr'
              ? "Investir et opérer : deux métiers, une seule ambition, la transformation industrielle durable."
              : "We invest and we operate. Two disciplines, one purpose: lasting industrial transformation."}
          </p>
        </div>
      </section>

      {/* Tab Navigation - Matching Team page style */}
      <section id="activities-tabs" className="border-b border-[#e5e5e5] py-6" style={{ backgroundColor: '#0a1f35' }}>
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="flex items-center justify-center gap-16 lg:gap-24">
            <button
              onClick={() => setActiveTab('investment')}
              className="px-6 lg:px-8 py-3 text-[11px] lg:text-[13px] font-medium transition-all duration-300 rounded"
              style={{
                fontFamily: "'Eurostile', 'Arial', sans-serif",
                fontWeight: activeTab === 'investment' ? 700 : 500,
                backgroundColor: activeTab === 'investment' ? '#6b7280' : 'transparent',
                color: activeTab === 'investment' ? '#ffffff' : 'rgba(255, 255, 255, 0.7)',
                border: activeTab === 'investment' ? 'none' : '1px solid rgba(255, 255, 255, 0.3)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}
            >
              {language === 'fr' ? 'Investissement' : 'Investment'}
            </button>
            <button
              onClick={() => setActiveTab('value-creation')}
              className="px-6 lg:px-8 py-3 text-[11px] lg:text-[13px] font-medium transition-all duration-300 rounded"
              style={{
                fontFamily: "'Eurostile', 'Arial', sans-serif",
                fontWeight: activeTab === 'value-creation' ? 700 : 500,
                backgroundColor: activeTab === 'value-creation' ? '#6b7280' : 'transparent',
                color: activeTab === 'value-creation' ? '#ffffff' : 'rgba(255, 255, 255, 0.7)',
                border: activeTab === 'value-creation' ? 'none' : '1px solid rgba(255, 255, 255, 0.3)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}
            >
              {language === 'fr' ? 'Création de valeur' : 'Value Creation'}
            </button>
          </div>
        </div>
      </section>

      {/* ==================== INVESTMENT SECTION ==================== */}
      {activeTab === 'investment' && (
        <section id="investment-section" className="py-16 lg:py-24 bg-technical-white">
          <div className="max-w-[1200px] mx-auto px-8 lg:px-12">
            {/* Section Title */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-16 text-center"
            >
              <h2
                className="text-2xl lg:text-3xl text-abyssal-blue mb-4"
                style={{ fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '0.02em' }}
              >
                {language === 'fr'
                  ? "Plateforme d'investissement avec une focalisation singulière sur l'écosystème industriel"
                  : "An investment platform with singular focus on the industrial ecosystem"}
              </h2>
              <div className="w-24 h-1 bg-light-blue mx-auto mt-6"></div>
            </motion.div>

            {/* Investment Content Blocks */}
            <div className="space-y-16 lg:space-y-24">
              {investmentBlocks.map((block, idx) => (
                <motion.div
                  key={block.id}
                  id={`block-${block.id}`}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: idx * 0.1 }}
                  className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${
                    idx % 2 === 1 ? 'lg:grid-flow-dense' : ''
                  }`}
                >
                  {/* Image */}
                  <div className={`overflow-hidden group ${idx % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                    <img
                      src={block.image}
                      alt={block.title}
                      className="w-full h-[300px] lg:h-[400px] object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>

                  {/* Content */}
                  <div className={idx % 2 === 1 ? 'lg:col-start-1' : ''}>
                    <h3
                      className="text-xl lg:text-2xl text-abyssal-blue mb-6"
                      style={{ fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '0.02em' }}
                    >
                      {block.title}
                    </h3>
                    <p
                      className="text-steel-grey mb-4"
                      style={{ fontFamily: "'Eurostile', 'Arial', sans-serif", lineHeight: 1.9, fontSize: '1.05rem' }}
                    >
                      {block.content}
                    </p>
                    {block.content2 && (
                      <p
                        className="text-steel-grey mt-6"
                        style={{ fontFamily: "'Eurostile', 'Arial', sans-serif", lineHeight: 1.9, fontSize: '1.05rem' }}
                      >
                        {block.content2}
                      </p>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Investment Section Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-4 justify-center mt-20"
            >
              <Link
                to="/activities/investing"
                className="group inline-flex items-center justify-center gap-3 px-8 py-4 transition-all duration-500 hover:scale-105"
                style={{ backgroundColor: '#2a6cae', fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", color: '#f4f5f7', fontSize: '12px', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase' }}
              >
                {language === 'fr' ? "Notre approche d'investissement" : 'Our investment approach'}
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/team"
                className="group inline-flex items-center justify-center gap-3 px-8 py-4 border-2 transition-all duration-500 hover:scale-105"
                style={{ borderColor: '#0a1f35', fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", color: '#0a1f35', fontSize: '12px', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase' }}
              >
                {language === 'fr' ? 'Notre équipe' : 'Our Team'}
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      {/* ==================== VALUE CREATION SECTION ==================== */}
      {activeTab === 'value-creation' && (
        <section id="value-creation-section" className="py-16 lg:py-24" style={{ backgroundColor: '#f8f9fa' }}>
          <div className="max-w-[1200px] mx-auto px-8 lg:px-12">
            {/* Section Title */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-16 text-center"
            >
              <h2
                className="text-2xl lg:text-3xl text-abyssal-blue mb-4"
                style={{ fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '0.02em' }}
              >
                {language === 'fr'
                  ? "Accompagner la transformation opérationnelle, de l'intérieur"
                  : "Driving operational transformation from the inside"}
              </h2>
              <div className="w-24 h-1 bg-light-blue mx-auto mt-6"></div>
            </motion.div>

            {/* Block 1 - Ocean Peak Management Overview with Image */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center mb-16 lg:mb-24"
            >
              {/* Image */}
              <div className="overflow-hidden group">
                <img
                  src={IMAGES.valueCreation}
                  alt="Ocean Peak Management"
                  className="w-full h-[300px] lg:h-[400px] object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>

              {/* Content */}
              <div>
                <h3
                  className="text-xl lg:text-2xl text-abyssal-blue mb-6"
                  style={{ fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '0.02em' }}
                >
                  {language === 'fr' ? 'Ocean Peak Management' : 'Ocean Peak Management'}
                </h3>
                <p
                  className="text-steel-grey mb-6"
                  style={{ fontFamily: "'Eurostile', 'Arial', sans-serif", lineHeight: 1.9, fontSize: '1.05rem' }}
                >
                  {language === 'fr'
                    ? "Nous accompagnons les entreprises dans leur transformation opérationnelle : stabilisation des flux, renforcement des fondamentaux, amélioration de la performance."
                    : "We support businesses through operational transformation: stabilising workflows, reinforcing fundamentals, and improving performance."}
                </p>
                <p
                  className="text-steel-grey mb-8"
                  style={{ fontFamily: "'Eurostile', 'Arial', sans-serif", lineHeight: 1.9, fontSize: '1.05rem' }}
                >
                  {language === 'fr'
                    ? "Nous intervenons au cœur des organisations, aux côtés des dirigeants et des équipes, là où les décisions sont réellement structurantes. Notre équipe connaît la réalité du terrain : elle a dirigé des usines, traversé des crises et reconstruit des trajectoires industrielles."
                    : "We work at the core of organisations, alongside leadership and their teams, where decisions have real structural impact. Our team knows the reality on the ground: they have run factories, managed through crises, and rebuilt industrial trajectories."}
                </p>
                <Link
                  to="/contact?type=partner"
                  className="group inline-flex items-center justify-center gap-3 px-8 py-4 transition-all duration-500 hover:scale-105"
                  style={{ backgroundColor: '#2a6cae', fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", color: '#f4f5f7', fontSize: '12px', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase' }}
                >
                  {language === 'fr' ? 'Devenir partenaire' : 'Partner with us'}
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </motion.div>

            {/* Divider */}
            <div className="w-full h-px bg-gray-300 mb-16 lg:mb-24"></div>

            {/* Block 2 - Recruiting Capability with Image */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center lg:grid-flow-dense"
            >
              {/* Image */}
              <div className="overflow-hidden group lg:col-start-2">
                <img
                  src={IMAGES.mechanicOverview}
                  alt="Recruiting Capability"
                  className="w-full h-[300px] lg:h-[400px] object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>

              {/* Content */}
              <div className="lg:col-start-1">
                <h3
                  className="text-xl lg:text-2xl text-abyssal-blue mb-6"
                  style={{ fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '0.02em' }}
                >
                  {language === 'fr' ? 'Ce qui nous différencie' : 'What Sets Us Apart'}
                </h3>
                <p
                  className="text-steel-grey mb-8"
                  style={{ fontFamily: "'Eurostile', 'Arial', sans-serif", lineHeight: 1.9, fontSize: '1.05rem' }}
                >
                  {language === 'fr'
                    ? "Les membres de l'équipe peuvent agir en soutien direct des dirigeants au sein des entreprises du portefeuille. Ce ne sont pas des conseillers externes : ils permettent d'accélérer la prise de décision, partagent la responsabilité des conséquences, et sont présents dans la durée. Cette proximité avec les opérations est notre marque de fabrique. Elle nous permet d'agir vite, de comprendre les contraintes réelles et de maintenir le cap là où d'autres se limitent à des recommandations."
                    : "Team members can act in direct support of leadership within portfolio companies. They are not external advisors: they accelerate decision-making, share accountability for the consequences, and are present for the long haul. This proximity to operations is our hallmark. It enables us to act fast, understand the real constraints, and hold the course where others stop at recommendations."}
                </p>
                <Link
                  to="/contact?type=work"
                  className="group inline-flex items-center justify-center gap-3 px-8 py-4 transition-all duration-500 hover:scale-105"
                  style={{ backgroundColor: '#2a6cae', fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", color: '#f4f5f7', fontSize: '12px', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase' }}
                >
                  {language === 'fr' ? 'Travailler avec nous' : 'Work with us'}
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      )}
    </div>
  );
};
